package ai.wagemix.web.components

import ai.wagemix.web.theme.WMAvatarVar
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMIconSmVar
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMPillCornerVar
import ai.wagemix.web.theme.WMPillGapVar
import ai.wagemix.web.theme.WMPillPaddingBlockVar
import ai.wagemix.web.theme.WMPillPaddingInlineVar
import androidx.compose.runtime.Composable
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import com.varabyte.kobweb.compose.css.WhiteSpace

/** Penpot `Wagemix / Badge`, property `Kind`. */
enum class WMBadgeKind {
    Neutral,
    Primary,
    /** Teal: AI-generated or projected. */
    AI,
    Warning,
    Error,
    Success,
    /** The plan badge in the sidebar and top bar (FREE / BASIC / PRO). */
    Plan
}

sealed interface WMBadgeKindMarker: ComponentKind

val WMBadgeStyle = CssStyle<WMBadgeKindMarker> {
    base {
        Modifier
            .inlineFlex()
            .gap(WMPillGapVar.value())
            .padding(topBottom = WMPillPaddingBlockVar.value(), leftRight = WMPillPaddingInlineVar.value())
            .borderRadius(WMPillCornerVar.value())
            .whiteSpace(WhiteSpace.NoWrap)
            .flexShrink(0)
    }
}

val WMNeutralBadgeVariant = WMBadgeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.surfaceContainer).color(colors.onSurfaceVariant)
    }
}
val WMPrimaryBadgeVariant = WMBadgeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.primaryContainer).color(colors.onPrimaryContainer)
    }
}
val WMAIBadgeVariant = WMBadgeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.secondaryContainer).color(colors.onSecondaryContainer)
    }
}
val WMWarningBadgeVariant = WMBadgeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.tertiaryContainer).color(colors.onTertiaryContainer)
    }
}
val WMErrorBadgeVariant = WMBadgeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.errorContainer).color(colors.onErrorContainer)
    }
}
val WMSuccessBadgeVariant = WMBadgeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.surfaceContainer).color(colors.success)
    }
}
val WMPlanBadgeVariant = WMBadgeStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier.backgroundColor(colors.primary).color(colors.onPrimary)
    }
}

fun WMBadgeKind.variant(): CssStyleVariant<WMBadgeKindMarker> = when (this) {
    WMBadgeKind.Neutral -> WMNeutralBadgeVariant
    WMBadgeKind.Primary -> WMPrimaryBadgeVariant
    WMBadgeKind.AI -> WMAIBadgeVariant
    WMBadgeKind.Warning -> WMWarningBadgeVariant
    WMBadgeKind.Error -> WMErrorBadgeVariant
    WMBadgeKind.Success -> WMSuccessBadgeVariant
    WMBadgeKind.Plan -> WMPlanBadgeVariant
}

/**
 * Penpot `Wagemix / Badge`: a pill with a caption-sized label and an optional leading glyph.
 */
@Composable
fun WMBadge(
    text: String,
    modifier: Modifier = Modifier,
    kind: WMBadgeKind = WMBadgeKind.Neutral,
    icon: WMIconGlyph? = null
) {
    Row(
        modifier = WMBadgeStyle.toModifier(kind.variant()).then(WMCaptionTextStyle.toModifier()).then(modifier),
        verticalAlignment = Alignment.CenterVertically
    ) {
        icon?.let { WMIcon(it, accessibilityString = null, size = WMIconSmVar.value()) }
        SpanText(text)
    }
}

val WMAvatarStyle = CssStyle {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .size(WMAvatarVar.value())
            .borderRadius(WMPillCornerVar.value())
            .backgroundColor(colors.primaryContainer)
            .color(colors.onPrimaryContainer)
            .flexShrink(0)
    }
}

/** Penpot `Wagemix / Avatar`: the user's initials in a brand-tinted circle. */
@Composable
fun WMAvatar(
    initials: String,
    accessibilityString: String,
    modifier: Modifier = Modifier,
    size: CSSLengthNumericValue = WMAvatarVar.value()
) {
    Box(
        modifier = WMAvatarStyle.toModifier().size(size).ariaLabel(accessibilityString).then(modifier),
        contentAlignment = Alignment.Center
    ) {
        SpanText(initials, WMLabelStrongTextStyle.toModifier())
    }
}
